import { ApiError, ContractError, ErrorBody, NetworkError } from './errors';
import { newIdempotencyKey } from './idempotency';
/**
 * Query strings, the way the API's contracts declare them.
 *
 * One parameter per key — `?status=DRAFT&audience=PARENT`, never a JSON object
 * under a single `query` param. An array repeats the key, which is what the
 * backend's `types` union expects (`?types=a&types=b`).
 */
function toQuery(query) {
    if (!query)
        return '';
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value === undefined || value === null || value === '')
            continue;
        if (Array.isArray(value)) {
            for (const item of value)
                params.append(key, item);
        }
        else {
            params.append(key, String(value));
        }
    }
    const qs = params.toString();
    return qs ? `?${qs}` : '';
}
export function createHttpClient({ baseUrl, auth, onSignOut }) {
    /*
     * Everything up to and including "the response is not an error": headers,
     * the token, the fetch, the 401 refresh-and-retry.
     *
     * Split out of `request` so that ONE caller can have the `Response` itself
     * rather than a parsed body — `GET /me/archive` is a file, and its
     * `Content-Disposition` filename is the part worth having. Doing that with a
     * bare `fetch` in the screen would have been three lines and would have lost
     * the refresh-and-retry below, which is the whole reason this client exists.
     */
    async function send(method, path, body, opts = {}, isRetry = false) {
        const headers = {
            Accept: 'application/json',
            ...(opts.locale ? { 'Accept-Language': opts.locale } : {}),
            ...opts.headers,
        };
        if (method === 'POST') {
            headers['Content-Type'] = 'application/json';
            /*
             * Required, not advisory: the backend refuses an unsafe operation without
             * it. With PUT and DELETE gone nothing is idempotent by protocol, and the
             * three places this is visible to a member — the checkout pay call, the
             * challenge day log, the post composer — are all ones a double-tap on a
             * flaky connection reaches naturally.
             */
            headers['Idempotency-Key'] = opts.idempotencyKey ?? newIdempotencyKey();
        }
        const token = auth?.accessToken;
        if (token)
            headers.Authorization = `Bearer ${token}`;
        let response;
        try {
            response = await fetch(`${baseUrl}${path}${toQuery(opts.query)}`, {
                method,
                headers,
                body: method === 'POST' ? JSON.stringify(body ?? {}) : undefined,
                signal: opts.signal,
                keepalive: opts.keepalive,
            });
        }
        catch (cause) {
            if (cause instanceof DOMException && cause.name === 'AbortError')
                throw cause;
            throw new NetworkError('The network request failed', cause);
        }
        if (response.status === 401 && auth && !isRetry) {
            /*
             * `hadSession` is load-bearing, not defensive.
             *
             * `refresh()` returns null both when a refresh FAILED and when there was
             * never a token to refresh with. Treating those the same fired
             * `onSignOut` on the very first request of a signed-out visitor — and
             * `onSignOut` clears the query cache, which wiped the in-flight session
             * query that was about to report the 401, so it reset to pending and
             * refetched, forever. The app hung on its boot skeleton for everyone who
             * was not already signed in, which is every visitor to the landing page.
             *
             * A 401 with no session is not a sign-out. It is simply not being signed
             * in, and nothing needs clearing.
             */
            const hadSession = auth.isAuthenticated;
            const refreshed = await auth.refresh();
            if (refreshed) {
                // Same idempotency key: this is a retry of one attempt, not a new one.
                return send(method, path, body, { ...opts, idempotencyKey: headers['Idempotency-Key'] }, true);
            }
            if (hadSession && !auth.isAuthenticated)
                onSignOut?.();
        }
        if (!response.ok)
            throw await toApiError(response);
        return response;
    }
    async function request(method, path, body, opts = {}) {
        const response = await send(method, path, body, opts);
        if (response.status === 204)
            return undefined;
        let payload;
        try {
            payload = await response.json();
        }
        catch (cause) {
            throw new NetworkError(`Response from ${path} was not JSON`, cause);
        }
        if (!opts.schema)
            return payload;
        const parsed = opts.schema.safeParse(payload);
        if (!parsed.success)
            throw new ContractError(path, parsed.error.issues);
        return parsed.data;
    }
    return {
        get: (path, opts) => request('GET', path, undefined, opts ?? {}),
        post: (path, body, opts) => request('POST', path, body, opts ?? {}),
        getResponse: (path, opts) => send('GET', path, undefined, opts ?? {}),
    };
}
async function toApiError(response) {
    let body;
    try {
        body = await response.json();
    }
    catch (cause) {
        return new NetworkError(`Request failed with ${response.status}`, cause);
    }
    const parsed = ErrorBody.safeParse(body);
    if (!parsed.success) {
        return new NetworkError(`Request failed with ${response.status}`, body);
    }
    return new ApiError(response.status, parsed.data);
}
//# sourceMappingURL=http.js.map